import React, { useCallback, useMemo, useRef, useState } from "react";
import { Box, InputAdornment } from "@mui/material";
import {
    StriimDataTable,
    StriimTypography,
    StriimInputField,
    StriimRadio
} from "@striim/striim-ui";
import SearchIcon from "@mui/icons-material/Search";
import WithTheme from "src/generic/theme/theme";
import App from "app";
import type { SchemaModel } from "../types";
import { getValue } from "./table-wrapper-selection.utils";
import type {
    ICellRendererParams,
    GridReadyEvent,
    FirstDataRenderedEvent,
    RowSelectedEvent,
    SelectionChangedEvent,
    GridApi,
    RowNode,
    ITooltipParams,
    GetRowIdParams
} from "ag-grid-community";
import { styles } from "./table-wrapper-selection.styles";

const TABLE_ROW_HEIGHT = 45;
const TABLE_MAX_ROW_COUNT = 9;
const TABLE_PADDING = 24;
const TABLE_HEIGHT = `calc(${TABLE_ROW_HEIGHT}px + (${TABLE_ROW_HEIGHT}px * ${TABLE_MAX_ROW_COUNT}) + (${TABLE_PADDING}px * 2) + 1px)`;

interface TableWrapperSelectionProps {
    schemas: SchemaModel[];
    whatIsInside: string;
    isChannel?: boolean;
    model?: any;
}

const CustomTooltip = (props: ITooltipParams) => {
    const data = props.data;
    const errors = getValue(data, "errors");

    if (!errors) return null;
    const errorArray = Array.isArray(errors) ? errors : [errors];

    return (
        <Box sx={styles.customTooltip}>
            {errorArray.map((error, index) => (
                <StriimTypography key={index} variant="caption3" color="greyscale.50" sx={{ display:"block" }}>
                    {index + 1}. {error}
                </StriimTypography>
            ))}
        </Box>
    );
};

const TableWrapperSelection: React.FC<TableWrapperSelectionProps> = ({
    schemas,
    whatIsInside,
    isChannel = false,
    model
}) => {
    const gridRef = useRef<GridApi<SchemaModel> | null>(null);
    const [searchText, setSearchText] = useState("");
    const [selectedCount, setSelectedCount] = useState(0);

	    const columnDefs = useMemo(
        () => [
            {
                headerName: "",
                width: 70,
                suppressMovable: true,
                lockPosition: true,
                headerCheckboxSelection: !isChannel,
                headerCheckboxSelectionFilteredOnly: !isChannel,
                checkboxSelection: !isChannel
                    ? (params: ICellRendererParams<SchemaModel>) => {
                          const schema = params.data;
                          const hasErrors = !!getValue(schema, "errors")?.length;
                          const isDisabled = getValue(schema, "isDisabled");
                          return !hasErrors && !isDisabled;
                      }
                    : false,
                cellRenderer: (params: ICellRendererParams<SchemaModel>) => {
                    const isDisabled = getValue(params.data, "isDisabled");
                    if (isDisabled) {
                        return (
                            <Box
                                component="img"
                                src="app/images/Block.svg"
                                sx={{ width: 20, height: 20, cursor: "not-allowed" }}
                            />
                        );
                    }

                    if (isChannel) {
                        return (
                            <StriimRadio
                                name="channel-selection"
                                checked={params.node.isSelected()}
                                onChange={() => {
                                    params.api.deselectAll();
                                    params.node.setSelected(true);
                                }}
                                sx={{ width: 14, height: 14 }}
                            />
                        );
                    }

                    return null;
                },
                cellStyle: {
                    display: "flex",
                    alignItems: "center"
                }
	            },
                {
	            headerName: `${whatIsInside} Name`,
                field: "schemaDisplayName",
                valueGetter: (params: any) => getValue(params.data, "schemaDisplayName"),
                flex: 1,
                sortable: true,
                suppressMovable: true,
                wrapText: true,
                autoHeight: true,
                cellRenderer: (params: ICellRendererParams<SchemaModel>) => {
                    const schema = params.data;
                    const hasErrors = !!schema.get("errors")?.length;
                    const isDisabled = schema.get("isDisabled");

                    return (
                        <StriimTypography
                            variant="body4"
                            sx={{
                                color:
                                    hasErrors || isDisabled
                                        ? "greyscale.500"
                                        : "greyscale.900"
                            }}
                        >
                            {schema.get("schemaDisplayName")}
                        </StriimTypography>
                    );
                }
            }
	        ],
	        [isChannel, whatIsInside]
    );

    const defaultColDef = useMemo(
        () => ({
            tooltipComponent: CustomTooltip,
            tooltipValueGetter: (params: any) => {
                const isDisabled = getValue(params.data, "isDisabled");
                if (!isDisabled) return null;
                return getValue(params.data, "errors");
            }
        }),
        []
    );

    const onGridReady = useCallback((params: GridReadyEvent<SchemaModel>) => {
        gridRef.current = params.api;
    }, []);

    const onFirstDataRendered = useCallback(
        (params: FirstDataRenderedEvent<SchemaModel>) => {
            if (isChannel) {
                const selectedChannelName = model.get("selectedChannel");
                if (selectedChannelName) {
                    let channelNode = null;
                    params.api.forEachNode((node: RowNode<SchemaModel>  ) => {
                        if (node.data.get("schemaName") === selectedChannelName) {
                            channelNode = node;
                        }
                    });
                    if (channelNode) {
                        params.api.setNodesSelected({ nodes: [channelNode], newValue: true });
                    }
                }
            } else {
                const schemasToSelect = [];
                params.api.forEachNode((node: RowNode<SchemaModel>) => {
                    if (node.data.get("isEnabled")) {
                        schemasToSelect.push(node);
                    }
                });
                if (schemasToSelect.length > 0) {
                    params.api.setNodesSelected({ nodes: schemasToSelect, newValue: true });
                }
            }

            const selectedCount =  params.api.getSelectedNodes().length;
            App.vent.trigger(selectedCount > 0 ? "appwizard:next:enable" : "appwizard:next:disable");
            setSelectedCount(selectedCount);
        },
        [isChannel, model]
    );

    const onRowSelected = useCallback(
        (event: RowSelectedEvent<SchemaModel>) => {
            const schema = event.data;
            const isSelected = event.node.isSelected();
            schema.set("isEnabled", isSelected);

            if (isChannel && isSelected) {
                const channelName = schema.get("schemaName");
                if (model) {
                    model.set("selectedChannel", channelName);
                }
            }

            if (isChannel) {
                event.api.refreshCells({ force: true });
            }
        },
        [isChannel, model]
    );

    const onSelectionChanged = useCallback((_event: SelectionChangedEvent) => {
        const selectedCount = _event.api.getSelectedNodes().length;
        App.vent.trigger(selectedCount > 0 ? "appwizard:next:enable" : "appwizard:next:disable");
        setSelectedCount(selectedCount);
    }, []);

    const getRowId = useCallback((params: GetRowIdParams<SchemaModel>) => {
        return params.data.get("id");
    }, []);

    return (
        <Box sx={styles.container} className="schema-selection-grid">
            <Box sx={styles.header}>
                <StriimTypography
                    variant="h2"
                    color="primary.900"
                    data-testid={'table-wrapper-selection-title'}
                >
                    Select the {whatIsInside}
                </StriimTypography>
                <StriimInputField
                    name="search-profile-name"
                    onChange={setSearchText}
                    placeholder={`Search ${whatIsInside}...`}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon opacity={0.45} />
                            </InputAdornment>
                        )
                    }}
                />
            </Box>
            <Box sx={styles.tableContainer(TABLE_HEIGHT, TABLE_PADDING)}>
                  <StriimTypography variant="body4" color="greyscale.700">
                   {whatIsInside} selected : {selectedCount} / {schemas.length}
                </StriimTypography>
                <Box sx={{height:"100%"}} data-testid={'table-wrapper-selection-table'}>
                    <StriimDataTable
                        ref={gridRef}
                        columns={columnDefs}
                        data={schemas}
                        quickFilterText={searchText}
                        getRowId={getRowId}
                        rowSelection={isChannel ? "single" : "multiple"}
                        suppressRowClickSelection={false}
                        rowMultiSelectWithClick={!isChannel}
                        headerHeight={TABLE_ROW_HEIGHT}
                        rowHeight={TABLE_ROW_HEIGHT}
                        onGridReady={onGridReady}
                        onFirstDataRendered={onFirstDataRendered}
                        onRowSelected={onRowSelected}
                        onSelectionChanged={onSelectionChanged}
                        isRowSelectable={(params) => {
                            if (isChannel) return true;
                            const isDisabled = getValue(params.data, "isDisabled");
                            return !isDisabled;
                        }}
                        rowClassRules={{
                            "row-selection-disabled": (params) => {
                                const isDisabled = getValue(params.data, "isDisabled");
                                return isDisabled;
                            }
                        }}
                        defaultColDef={defaultColDef}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default WithTheme(TableWrapperSelection);
